import { tw } from "@/utils";
import { ICUResource } from "../api";
import { bedCodeToName } from "@/data/beds";

export const ICUBarChart = ({ resource }: { resource: ICUResource }) => {
  return (
    <div className="relative w-full h-full">
      {/* Y축 선 */}
      <div className="absolute z-10">
        <ICUGrid columns={bedTypeCodes.length} />
        <div className="absolute left-[1px] top-[1px]">
          {[100, 80, 60, 40, 20, 0].map((criteria, index) => (
            <div
              className="absolute left-[8px] text-sm font-regular text-gray-g100"
              style={{ top: `${index * 54 + 8}px` }}
            >
              {criteria}
            </div>
          ))}
        </div>
      </div>

      {/* 막대 영역 */}
      <div
        className={tw(
          "relative flex h-full w-full pl-[56px]",
          "border-solid border-[1px] border-white rounded-[8px]"
        )}
      >
        {transformToBedEntries(resource).map((data) => (
          <ICUSectionGrid data={data} />
        ))}
      </div>
    </div>
  );
};

export const ICUGrid = (props: { columns: number }) => {
  const rows = 5;
  const gridColumns = props.columns * 2 + 1;

  return (
    <div className="border-solid border-[1px] border-gray-g50 rounded-[8px]">
      <div
        className={tw("grid bg-transparent overflow-hidden")}
        style={{
          gridTemplateColumns: `repeat(${gridColumns}, 56px)`,
          gridTemplateRows: `repeat(${rows}, 54px)`,
        }}
      >
        {Array.from({ length: rows * gridColumns }).map((_, idx) => {
          const isFirstColumn = idx % gridColumns === 0;
          const isFirstRow = Math.floor(idx / gridColumns) === 0;

          return (
            <div
              key={idx}
              className={tw(
                "w-full h-full",
                !isFirstColumn && "border-l border-gray-g50",
                !isFirstRow && "border-t border-gray-g50"
              )}
            />
          );
        })}
      </div>
    </div>
  );
};

export const ICUSectionGrid = (props: { data: BedEntry }) => {
  const { data } = props;
  const previousRatio =
    data.rltmPastBeds && data.stdPastBeds
      ? ((data.rltmPastBeds / data.stdPastBeds) * 100).toFixed(1)
      : null;
  const currentRatio =
    data.rltmCurrentBeds && data.stdCurrentBeds
      ? ((data.rltmCurrentBeds / data.stdCurrentBeds) * 100).toFixed(1)
      : null;

  return (
    <div className="flex flex-col">
      {/* 상단 두 줄 (270 높이) */}
      <div className="flex">
        <div className="flex items-end justify-center w-[56px] h-[270px]">
          <div
            className="w-[44px] bg-gray-g30"
            style={{ height: `${previousRatio}%` }}
          ></div>
        </div>
        <div className="flex items-end justify-center w-[56px] h-[270px]">
          <div
            className="w-[44px] bg-purple-100"
            style={{ height: `${currentRatio}%` }}
          ></div>
        </div>
      </div>

      {/* 중간 두 줄 (24 높이) */}
      <div className="flex border-solid border-l-[1px] border-gray-g30">
        <div
          className={tw(
            "flex items-center justify-center w-[56px] h-[24px] bg-white",
            "border-solid border-gray-g50 rounded-[8px]",
            "text-sm font-medium text-gray-g100"
          )}
        >
          {previousRatio && `${previousRatio}%`}
        </div>
        <div
          className={tw(
            "flex items-center justify-center w-[55px] h-[24px] bg-white",
            "border-solid border-gray-g50 rounded-[8px]",
            "text-sm font-medium text-gray-g100"
          )}
        >
          {currentRatio && `${currentRatio}%`}
        </div>
      </div>

      {/* 하단 하나 (전체 가로 차지) */}
      <div
        className={tw(
          "w-full h-[72px]",
          "border-solid border-l-[1px] border-gray-g30"
        )}
      >
        <div className="flex items-center justify-center w-full h-[50px] gap-[8px]">
          <div
            className={tw(
              "w-[8px] aspect-square rounded-full",
              congestionColor(Number(currentRatio))
            )}
          />
          <div>
            {
              bedCodeToName[data.bedCode].split("] ")[
                bedCodeToName[data.bedCode].split("] ").length - 1
              ]
            }
          </div>
        </div>
        <div
          className={tw(
            "flex items-center justify-center w-full h-[22px]",
            "text-base font-regular text-gray-g500"
          )}
        >
          {currentRatio ? (
            <>
              <span className="text-purple-100">{data.rltmCurrentBeds}</span>/
              {data.stdCurrentBeds}
            </>
          ) : (
            "정보없음"
          )}
        </div>
      </div>
    </div>
  );
};

const congestionColor = (ratio: number) => {
  if (isNaN(ratio)) return "bg-red";
  if (ratio > 100 || ratio < 0) return "bg-gray-g100";

  if (ratio >= 80) {
    return "bg-green";
  } else if (ratio >= 50) {
    return "bg-yellow";
  } else {
    return "bg-red";
  }
};

const bedTypeCodes: string[] = [
  "o017",
  "o018",
  "o009",
  "o008",
  "o006",
  "o015",
  "o011",
  "o013",
  "o007",
  "o012",
  "o016",
];

interface BedEntry {
  bedCode: BedCode;
  stdPastBeds: number | null;
  rltmPastBeds: number | null;
  stdCurrentBeds: number | null;
  rltmCurrentBeds: number | null;
}

type BedCode =
  | "o017"
  | "o018"
  | "o009"
  | "o008"
  | "o006"
  | "o015"
  | "o011"
  | "o013"
  | "o007"
  | "o012"
  | "o016";

function transformToBedEntries(resource: ICUResource): BedEntry[] {
  const bedCodes: BedCode[] = [
    "o017",
    "o018",
    "o009",
    "o008",
    "o006",
    "o015",
    "o011",
    "o013",
    "o007",
    "o012",
    "o016",
  ];

  return bedCodes.map((code) => ({
    bedCode: code,
    stdPastBeds: resource.stdPastBeds[code] ?? null,
    rltmPastBeds: resource.rltmPastBeds[code] ?? null,
    stdCurrentBeds: resource.stdCurrentBeds[code] ?? null,
    rltmCurrentBeds: resource.rltmCurrentBeds[code] ?? null,
  }));
}
